/**
 * Pending Changes Store
 * 
 * Manages local draft changes for Library, Template, and Document items.
 * Changes are persisted to IndexedDB and only synced to backend on explicit save.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { Version } from "./storeTypes";

// Item types that support pending changes
export type PendingChangeItemType = 'library' | 'template' | 'document';

// Types for pending changes
export interface PendingChange {
  id: string; // Unique ID for this pending change (same as item ID)
  itemId: string;
  itemType: PendingChangeItemType;
  gameId: string;
  originalVersion: Version | null;
  originalData: any;
  pendingData: any;
  modifiedCells: string[]; // Cell keys that were modified
  modifiedAt: string; // ISO timestamp
  createdAt: string; // ISO timestamp when first modified
}

export interface CellModification {
  cellKey: string;
  oldValue: any;
  newValue: any;
}

// Create the pending changes store using GenericStore for IndexedDB persistence
export const pendingChangesStore = storeRegistry.getStore<PendingChange>({
  key: 'pending-changes',
  ttlMs: 1000 * 60 * 60 * 24 * 7, // 7 days - keep pending changes for a week
});

/**
 * Get all pending changes for a specific game and item type
 */
export function getPendingChanges(gameId: string, itemType?: PendingChangeItemType): PendingChange[] {
  const allChanges = pendingChangesStore.getData();
  return allChanges.filter(change => 
    change.gameId === gameId && 
    (itemType ? change.itemType === itemType : true)
  );
}

/**
 * Get a specific pending change by item ID
 */
export function getPendingChangeById(itemId: string): PendingChange | undefined {
  const allChanges = pendingChangesStore.getData();
  return allChanges.find(change => change.itemId === itemId);
}

/**
 * Check if an item has pending changes
 */
export function hasPendingChanges(itemId: string): boolean {
  return getPendingChangeById(itemId) !== undefined;
}

/**
 * Check if a specific cell has been modified
 */
export function isCellModified(itemId: string, cellKey: string): boolean {
  const pendingChange = getPendingChangeById(itemId);
  return pendingChange?.modifiedCells.includes(cellKey) ?? false;
}

/**
 * Get all modified cells for an item
 */
export function getModifiedCells(itemId: string): string[] {
  const pendingChange = getPendingChangeById(itemId);
  return pendingChange?.modifiedCells ?? [];
}

/**
 * Add or update a pending change
 * This is called when a cell value changes locally
 */
export function addPendingChange(
  itemId: string,
  itemType: PendingChangeItemType,
  gameId: string,
  originalVersion: Version | null,
  originalData: any,
  pendingData: any,
  cellKey: string
): void {
  const allChanges = pendingChangesStore.getData();
  const existingIndex = allChanges.findIndex(c => c.itemId === itemId);
  const now = new Date().toISOString();
  
  if (existingIndex >= 0) {
    // Update existing pending change
    const existing = allChanges[existingIndex];
    const updatedModifiedCells = existing.modifiedCells.includes(cellKey)
      ? existing.modifiedCells
      : [...existing.modifiedCells, cellKey];
    
    const updatedChange: PendingChange = {
      ...existing,
      pendingData,
      modifiedCells: updatedModifiedCells,
      modifiedAt: now,
    };
    
    const newData = [...allChanges];
    newData[existingIndex] = updatedChange;
    pendingChangesStore.setData(newData);
  } else {
    // Create new pending change
    const newChange: PendingChange = {
      id: itemId, // Use item ID as the pending change ID
      itemId,
      itemType,
      gameId,
      originalVersion,
      originalData,
      pendingData,
      modifiedCells: [cellKey],
      modifiedAt: now,
      createdAt: now,
    };
    
    pendingChangesStore.setData([...allChanges, newChange]);
  }
}

/**
 * Remove a pending change (after successful save or discard)
 */
export function removePendingChange(itemId: string): void {
  const allChanges = pendingChangesStore.getData();
  const newData = allChanges.filter(c => c.itemId !== itemId);
  pendingChangesStore.setData(newData);
}

/**
 * Remove all pending changes for a game and item type
 */
export function removeAllPendingChanges(gameId: string, itemType?: PendingChangeItemType): void {
  const allChanges = pendingChangesStore.getData();
  const newData = allChanges.filter(change => 
    !(change.gameId === gameId && (itemType ? change.itemType === itemType : true))
  );
  pendingChangesStore.setData(newData);
}

/**
 * Get the count of pending changes for a game and item type
 */
export function getPendingChangesCount(gameId: string, itemType?: PendingChangeItemType): number {
  return getPendingChanges(gameId, itemType).length;
}

/**
 * Subscribe to pending changes store updates
 */
export function subscribeToPendingChanges(callback: (changes: PendingChange[]) => void): () => void {
  return pendingChangesStore.subscribe(callback);
}

/**
 * Clear all pending changes (useful for logout/reset)
 */
export async function clearAllPendingChanges(): Promise<void> {
  await pendingChangesStore.clearCache();
}


















